import React, { useEffect, useRef } from "react";
import { Chart } from "chart.js";
import _ from "lodash";
import { ChartJsContainer } from "./ChartJsContainer";
import ChartStack100 from "chartjs-plugin-stacked100";
import ChartDataLabels from "chartjs-plugin-datalabels";
import ChartDeferred from "chartjs-plugin-deferred";
import * as d3 from "d3";
import styles from "./StackHorizontalChart.module.scss";
import ReactDOM from "react-dom";
import uuid from "react-uuid";
import { D3PseudoSelect, locateTooltip, roundInSum } from "../misc";
import { roundValue, Unit } from "../processors";
import { formatNumber } from "../../utils";

Chart.plugins.register(ChartStack100);
Chart.plugins.register(ChartDataLabels);
Chart.plugins.register(ChartDeferred);

const options = (tooltip?: { current: string }, chartValues?: number[], unit?: Unit) => {
    return {
        onHover: function (event, ds) {
            if (ds && ds[0] && ds[0]._chart && ds[0]._chart.ctx) {
                const cv = event.target;
                const element = cv.parentElement as HTMLElement;
                let borderElement: HTMLElement = element.querySelector(".border-element");
                if (!borderElement) {
                    const borderElement = document.createElement("div");
                    borderElement.classList.add("border-element");
                    element.appendChild(borderElement);
                }

                if (borderElement) {
                    borderElement.style.left = ds[0]._model.base + "px";
                    borderElement.style.width = ds[0]._model.x - ds[0]._model.base + "px";
                }

                if (tooltip?.current == null) return;

                const tooltipElement = document.getElementById(tooltip?.current);

                if (!tooltipElement) return;

                var dsIndex = ds[0]._datasetIndex;
                const chartValue = roundValue(chartValues?.[dsIndex], unit);

                const background = ((ds[0]._chart.data || {}).datasets[dsIndex] || {}).backgroundColor || "white";

                const label = ds?.[0]?._chart?.data?.datasets?.[dsIndex].label ?? "";
                const values = roundInSum(ds?.[0]?._chart?.data?.calculatedData?.map(d => d?.[0] ?? 0) ?? [], 100);

                const _value = values[dsIndex];
                const value = _value == null ? "" : `${_value < 1 ? "< 1" : _value}`;

                ReactDOM.render(
                    <div>
                        <div
                            style={{
                                float: "left",
                                backgroundColor: background,
                                width: "1rem",
                                height: "1rem",

                                marginRight: "0.5rem",
                                marginTop: "0.2rem"
                            }}
                        ></div>
                        <span>
                            {label}: {value}
                            {"%"} {` `}{" "}
                            {`${
                                chartValue?.value != null
                                    ? `(${formatNumber(chartValue?.value)}${
                                          chartValue?.unit != null && (chartValue?.unit as Unit) !== "Count"
                                              ? ` ${chartValue?.unit}`
                                              : ""
                                      })`
                                    : ""
                            }`}
                        </span>
                    </div>,
                    tooltipElement
                );

                locateTooltip({
                    d3Tooltip: d3.select(tooltipElement) as unknown as D3PseudoSelect,
                    event,
                    options: {
                        opacity: true
                    }
                });
            } else {
                const cv = event.target;
                const element = cv.parentElement as HTMLElement;

                let borderElement: HTMLElement = element.querySelector(".border-element");
                if (borderElement) {
                    borderElement.remove();
                }

                const tooltipElement = document.getElementById(tooltip?.current);

                if (tooltipElement) {
                    tooltipElement.style.display = "none";
                }
            }
        },
        layout: {
            padding: {
                left: 0,
                right: 0,
                top: 0,
                bottom: 43
            }
        },
        hover: {
            mode: "dataset",
            intersect: true,
            axis: "x"
        },
        tooltips: {
            enabled: false,
            mode: "nearest",
            titleFontFamily: "'Lato', sans-serif",
            bodyFontFamily: "'Lato', sans-serif",
            footerFontFamily: "'Lato', sans-serif"
        },
        legend: {
            display: false
        },

        scales: {
            yAxes: [
                {
                    display: false,
                    barPercentage: 1,
                    categoryPercentage: 1,

                    ticks: {
                        mirror: true
                    }
                }
            ],
            xAxes: [
                {
                    display: false
                }
            ]
        },
        plugins: {
            flexiTickArea: false,
            stacked100: {
                enable: true,
                replaceTooltipLabel: false
            },
            datalabels: {
                color: "black",
                font: {
                    family: "'Lato', sans-serif",
                    size: "10.8",
                    weight: "400"
                },
                formatter: (value, context) => {
                    if (!(context && context.dataset && context.dataset.label)) return [];

                    if (
                        !(
                            context &&
                            context.chart &&
                            context.chart.config &&
                            context.chart.config.data &&
                            context.chart.config.data.datasets
                        )
                    )
                        return [];

                    const total = _.sum(
                        context.chart.config.data.datasets.map(d => (d && d.data && d.data.length > 0 ? d.data[0] : 0))
                    );

                    const w = context.chart.width;
                    const current =
                        context.dataset && context.dataset.data && context.dataset.data.length > 0
                            ? context.dataset.data[0]
                            : 0;
                    const mW = (current / total) * w;

                    const label = context.dataset.label;
                    const ctx = context.chart.ctx;

                    let doneFirstLine = false;
                    let doneSecondLine = false;
                    let firstLine = [];
                    let secondLine = [];
                    let firstLineWidth = 0;
                    let secondLineWidth = 0;

                    let words = label.split(" ");
                    words.forEach(w => {
                        if (!doneFirstLine) {
                            if (firstLineWidth === 0) firstLineWidth += ctx.measureText(w).width;
                            else firstLineWidth += ctx.measureText(" " + w).width;

                            if (firstLineWidth > mW) {
                                doneFirstLine = true;
                            } else {
                                firstLine.push(w);
                            }
                        }

                        if (doneFirstLine && !doneSecondLine && firstLine.length > 0) {
                            if (secondLineWidth === 0) secondLineWidth += ctx.measureText(w).width;
                            else secondLineWidth += ctx.measureText(" " + w).width;

                            if (secondLineWidth > mW) {
                                doneSecondLine = true;
                            } else {
                                secondLine.push(w);
                            }
                        }

                        if (doneSecondLine) return;
                    });

                    let strFirstLine = _.join(firstLine, " ");

                    let strSecondLine = _.join(secondLine, " ");
                    let revSecondLine = "";

                    for (var i = 0; i < strSecondLine.length; i++) {
                        revSecondLine += strSecondLine[i];

                        if (ctx.measureText(revSecondLine).width > mW) break;
                    }

                    if (strSecondLine.trim() !== "") {
                        strSecondLine =
                            revSecondLine === strSecondLine
                                ? strSecondLine
                                : revSecondLine.length <= 3
                                ? "..."
                                : revSecondLine.substr(0, revSecondLine.length - 3) + "...";
                    }

                    if (strSecondLine.trim() === "") {
                        return strFirstLine === label
                            ? strFirstLine
                            : strFirstLine.length <= 3
                            ? "..."
                            : strFirstLine.substr(0, strFirstLine.length - 3) + "...";
                    }

                    //return initText == label? "hello\nworld": "...";
                    return _.join([firstLine, secondLine], "\n");
                },
                clip: false
            },
            doughnutlabel: false,
            labels: false,
            deferred: {
                yOffset: "30%",
                delay: 500
            }
        },
        maintainAspectRatio: false
    };
};

interface StackHorizontalChartProps {
    values?: number[];
    unit?: Unit;
    labels?: string[];
    colors?: string[];
    onHover?: () => {};
}
export const StackHorizontalChart: React.FC<StackHorizontalChartProps> = ({ values, unit, labels, colors }) => {
    const ref = useRef();
    const tooltip = useRef<string>(undefined);
    const _options = options(tooltip, values, unit);
    const data = _.zip(values, labels, colors);

    const chart = {
        type: "horizontalBar",
        data: {
            datasets: data.map(d => {
                const value = d[0];
                const label = d[1];
                const color = d[2];
                return {
                    data: [value],
                    datalabels: { align: "bottom", anchor: "center", offset: 25 },
                    backgroundColor: color,
                    borderColor: color,
                    hoverBackgroundColor: color,
                    label
                };
            }),
            labels: []
        },
        options: _options,
        plugins: [ChartStack100, ChartDataLabels, ChartDeferred]
    };

    useEffect(() => {
        const id = uuid();

        d3.select("body").append("div").attr("class", "vis-tooltip").attr("id", id);
        tooltip.current = id;

        return () => {
            document.getElementById(id).remove();
        };
    }, []);

    if (!values || !values?.length || values?.length !== labels?.length || values?.length !== colors?.length)
        return null;

    return <ChartJsContainer ref={ref} chart={chart} className={styles.horizontalBar} />;
};
